
import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, FileUp, FileX, Calculator, BookOpen } from "lucide-react";

const WordMockTestGenerator: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("upload");
  const [isUploading, setIsUploading] = useState(false);
  
  // This is a placeholder implementation
  // The full implementation would handle file uploads and processing
  const handleUpload = async () => {
    setIsUploading(true);
    
    try {
      toast.info("This is a demo implementation. File upload is not fully implemented.");
      
      setTimeout(() => {
        toast.success("Mock test questions created with math rendering support.");
        
        // Navigate to the test page with a demo ID
        navigate(`/mock-test/demo-math-test`);
      }, 2000);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to process file.");
    } finally {
      setIsUploading(false);
    }
  };
  
  return (
    <div className="container mx-auto py-8 px-4 max-w-4xl">
      <Card>
        <CardHeader>
          <CardTitle>Word Mock Test Generator</CardTitle>
          <CardDescription>
            Upload Word documents or PDFs with math formulas to create interactive mock tests
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid grid-cols-2 mb-8">
              <TabsTrigger value="upload">Upload Document</TabsTrigger>
              <TabsTrigger value="samples">Sample Tests</TabsTrigger>
            </TabsList>
            
            <TabsContent value="upload" className="space-y-4">
              <div className="border-2 border-dashed rounded-lg p-10 text-center bg-muted/30">
                <div className="mb-4 flex justify-center">
                  <FileUp className="h-12 w-12 text-muted-foreground" />
                </div>
                
                <h3 className="text-lg font-semibold mb-2">Upload Test Document</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Drag and drop or click to upload Word documents (.docx) or PDFs with math formulas
                </p>
                
                <Button 
                  variant="outline" 
                  className="mt-2" 
                  size="lg"
                  disabled={isUploading}
                  onClick={handleUpload}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {isUploading ? "Uploading..." : "Upload Document"}
                </Button>
                
                <div className="mt-6 text-xs text-muted-foreground">
                  <p>Supports LaTeX math notation in the format $...$ or $$...$$</p>
                  <p className="mt-1">Maximum file size: 10MB</p>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="samples" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">JEE Mathematics</CardTitle>
                    <CardDescription>
                      Mock test with advanced calculus and algebra problems
                    </CardDescription>
                  </CardHeader>
                  <CardFooter>
                    <Button 
                      className="w-full" 
                      variant="outline" 
                      onClick={() => {
                        toast.success("Loading sample math test");
                        navigate(`/mock-test/sample-math-test`);
                      }}
                    >
                      <Calculator className="h-4 w-4 mr-2" />
                      Open Sample
                    </Button>
                  </CardFooter>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Physics Formulas</CardTitle>
                    <CardDescription>
                      Test with physics equations and derivations
                    </CardDescription>
                  </CardHeader>
                  <CardFooter>
                    <Button 
                      className="w-full" 
                      variant="outline" 
                      onClick={() => {
                        toast.success("Loading sample physics test");
                        navigate(`/mock-test/sample-physics-test`);
                      }}
                    >
                      <BookOpen className="h-4 w-4 mr-2" />
                      Open Sample
                    </Button>
                  </CardFooter>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default WordMockTestGenerator;
